import React, { useEffect, useMemo, useState } from "react";
import { ReloadOutlined } from "@ant-design/icons";
import { getAllAsistencias, ESTADOS_ASISTENCIA } from "../../services/asistenciasService";
import { listGrupos } from "../../services/gruposService"; // Importa el servicio
import { toast } from "react-toastify";
import Modal from "antd/es/modal/Modal";

// === Constantes ===
const TIPO_EVENTO = {
  ENTRENAMIENTO: "entrenamiento",
  TORNEO: "torneo",
};

// === Helpers ===
function clsx(...c) { return c.filter(Boolean).join(" "); }

function Badge({ children, variant = "default" }) {
  const variants = {
    default: "bg-slate-100 text-slate-700 border-slate-200",
    success: "bg-emerald-50 text-emerald-700 border-emerald-200",
    danger: "bg-rose-50 text-rose-700 border-rose-200",
    info: "bg-blue-50 text-blue-700 border-blue-200",
    warning: "bg-amber-50 text-amber-700 border-amber-200",
    muted: "bg-slate-50 text-slate-500 border-slate-200",
  };
  return (
    <span className={clsx("inline-flex items-center gap-1 rounded-full border text-xs px-2 py-1", variants[variant])}>{children}</span>
  );
}

function ListSkeleton({ rows = 6 }) {
  return (
    <div className="grid grid-cols-1 gap-4">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="h-32 rounded-2xl border border-slate-200 bg-white overflow-hidden">
          <div className="animate-pulse h-full bg-slate-100" />
        </div>
      ))}
    </div>
  );
}

// === Íconos inline ===
function InboxIcon(props) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      {...props}
    >
      <path d="M22 12h-6l-2 3h-4l-2-3H2" />
      <path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11Z" />
    </svg>
  );
}

export default function AsistenciasView() {
  const [asistencias, setAsistencias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [viewMode, setViewMode] = useState("cards"); // "cards" | "list"
  const [query, setQuery] = useState("");
  const [tipo, setTipo] = useState("todos");
  const [grupo, setGrupo] = useState("todos");
  const [grupos, setGrupos] = useState([]); // Estado para almacenar los grupos
  const [selected, setSelected] = useState(null);
  const [showDetail, setShowDetail] = useState(false);

  // Cargar asistencias al montar
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoading(true);
        setError("");
        const data = await getAllAsistencias();
        if (!mounted) return;
        setAsistencias(data || []);
      } catch (e) {
        console.error(e);
        if (mounted) {
          setError(e?.message || "Error cargando asistencias");
          toast.error(e?.message || "Error cargando asistencias");
        }
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  // Cargar grupos al montar
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const data = await listGrupos();
        if (!mounted) return;
        setGrupos(data.filter((grupo) => grupo.activo)); // Filtrar grupos activos
      } catch (e) {
        console.error(e);
        toast.error("Error cargando grupos");
      }
    })();
    return () => { mounted = false; };
  }, []);

  // Filtrado en cliente (case-insensitive)
  const filtered = useMemo(() => {
    const term = (query || "").toString().toLowerCase();
    return asistencias.filter((a) => {
      const haystack = `${a.deportista?.nombres || ""} ${a.deportista?.apellidos || ""} ${a.deportista?.cedula || ""} ${a.deportista?.grupo_nombre || ""} ${a.evento?.nombre || ""}`;
      const matchesQuery = haystack.toLowerCase().includes(term);
      const matchesTipo = tipo === "todos" || (a.evento?.tipo || "").toString().toLowerCase() === (tipo || "").toString().toLowerCase();
      const matchesGrupo = grupo === "todos" || (a.deportista?.grupo_nombre || "").toString().toLowerCase() === (grupo || "").toString().toLowerCase();
      return matchesQuery && matchesTipo && matchesGrupo;
    });
  }, [asistencias, query, tipo, grupo]);

  function openDetail(asistencia) {
    setSelected(asistencia);
    setShowDetail(true);
  }

  return (
    <div className="mx-auto max-w-7xl p-4 md:p-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
        <div>
          <h1 className="text-xl md:text-2xl font-semibold">Asistencias</h1>
          <p className="text-slate-600 text-sm">Registro de asistencias por evento.</p>
        </div>
      </div>

      <div className="flex flex-col md:flex-row md:items-center gap-3 mb-6">
        <div className="flex-1">
          <input 
            className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-4 focus:ring-blue-100" 
            placeholder="Buscar por deportista, evento, grupo..." 
            value={query} 
            onChange={(e) => setQuery(e.target.value)} 
          />
        </div>

        <select 
          className="rounded-xl border border-slate-300 px-3 py-2 text-sm" 
          value={tipo} 
          onChange={(e) => setTipo(e.target.value)}
        >
          <option value="todos">Todos los tipos</option>
          <option value={TIPO_EVENTO.ENTRENAMIENTO}>Entrenamiento</option>
          <option value={TIPO_EVENTO.TORNEO}>Torneo</option>
        </select>

        <select 
          className="rounded-xl border border-slate-300 px-3 py-2 text-sm" 
          value={grupo} 
          onChange={(e) => setGrupo(e.target.value)}
        >
          <option value="todos">Todos los grupos</option>
          {grupos.map((g) => (
            <option key={g.id} value={g.nombre}>{g.nombre}</option>
          ))}
        </select>

        {/* View mode switch - style copied from Colaboradores.js */}
        <div className="flex items-center gap-1 p-1 bg-slate-100 rounded-lg">
          <button
            onClick={() => setViewMode("cards")}
            className={clsx(
              "px-3 py-1.5 rounded-md text-sm font-medium transition-all",
              viewMode === "cards"
                ? "bg-white text-blue-600 shadow-sm"
                : "text-slate-600 hover:text-slate-900"
            )}
            title="Vista de tarjetas"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
            </svg>
          </button>
          <button
            onClick={() => setViewMode("list")}
            className={clsx(
              "px-3 py-1.5 rounded-md text-sm font-medium transition-all",
              viewMode === "list"
                ? "bg-white text-blue-600 shadow-sm"
                : "text-slate-600 hover:text-slate-900"
            )}
            title="Vista de lista"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h.01M4 12h.01M4 18h.01M8 6h13M8 12h13M8 18h13" />
            </svg>
          </button>
        </div>
        
        <button
          onClick={async () => {
            setLoading(true);
            try {
              const data = await getAllAsistencias();
              setAsistencias(data);
            } catch (err) {
              console.error(err);
              setError("Error al cargar asistencias");
            } finally {
              setLoading(false);
            }
          }}
          disabled={loading}
          className="inline-flex items-center gap-2 rounded-xl bg-slate-100 text-slate-700 px-3 py-2 text-sm font-medium hover:bg-slate-200 active:scale-[.99] transition disabled:opacity-50 disabled:cursor-not-allowed"
          title="Recargar datos"
        >
          <ReloadOutlined className="text-base" spin={loading} />
        </button>
      </div>

      {error && (
        <div className="mb-4 rounded-xl border border-rose-200 bg-rose-50 text-rose-700 px-3 py-2 text-sm flex items-center gap-2">
          <span className="text-base">⚠️</span>
          <span>{error}</span>
        </div>
      )}

      {loading && <ListSkeleton rows={6} />}

      {/* Cards view */}
      {!loading && viewMode === "cards" && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.length === 0 && (
            <div className="col-span-full py-10 text-center text-slate-500 text-sm">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-slate-100 mb-4">
                <InboxIcon className="w-8 h-8 text-slate-400" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-1">No se encontraron asistencias</h3>
              <p className="text-sm text-slate-500">
                {query ? "Intenta ajustar los filtros de búsqueda" : "No hay asistencias registradas"}
              </p>
            </div>
          )}
          {filtered.map((asistencia) => (
            <article 
              key={asistencia.id} 
              className="w-full overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm hover:shadow-md transition">
              <div className="p-4">
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div className="flex-1">
                    <h3 className="text-base font-semibold">
                      {asistencia.deportista?.nombres} {asistencia.deportista?.apellidos}
                    </h3>
                    <p className="text-xs text-slate-500">
                      CI: {asistencia.deportista?.cedula || "—"} • {asistencia.deportista?.grupo_nombre || "Sin grupo"}
                    </p>
                  </div>
                  <Badge variant={
                    asistencia.estado_asistencia === ESTADOS_ASISTENCIA.PRESENTE ? "success" :
                    asistencia.estado_asistencia === ESTADOS_ASISTENCIA.AUSENTE ? "danger" :
                    asistencia.estado_asistencia === ESTADOS_ASISTENCIA.TARDANZA ? "warning" :
                    asistencia.estado_asistencia === ESTADOS_ASISTENCIA.JUSTIFICADO ? "info" : "default"
                  }>
                    {asistencia.estado_asistencia}
                  </Badge>
                </div>
                <div className="text-sm text-slate-700 space-y-1">
                  <div className="flex items-center gap-2">
                    <span className="text-slate-500 font-medium">Evento:</span>
                    <span className="font-medium">{asistencia.evento?.nombre || "—"}</span>
                  </div>
                  <div className="flex items-center gap-4 text-xs">
                    <span>📅 {asistencia.evento?.fecha_inicio || "—"}</span>
                    <span>📍 {asistencia.evento?.ubicacion || "—"}</span>
                  </div>
                </div>
                <button
                  onClick={() => openDetail(asistencia)}
                  className="w-full inline-flex items-center justify-center rounded-xl border border-slate-600 text-slate-700 px-3 py-2 text-sm hover:bg-slate-50 hover:border-slate-500 active:scale-[.99] transition mt-4"
                >
                  Ver detalles
                </button>
              </div>
            </article>
          ))}
        </div>
      )}

      {/* List (table) view */}
      {!loading && viewMode === "list" && (
        <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Deportista</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Cédula</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Grupo</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Evento</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Fecha</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Estado</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600 uppercase tracking-wider">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {filtered.map((asistencia) => (
                  <tr key={asistencia.id} className="hover:bg-slate-50 transition-colors">
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center gap-3">
                        <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-blue-100 text-blue-600 font-semibold text-sm border border-slate-200">
                          {asistencia.deportista?.nombres?.charAt(0)}{asistencia.deportista?.apellidos?.charAt(0)}
                        </div>
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-slate-900 truncate">{asistencia.deportista?.nombres} {asistencia.deportista?.apellidos}</div>
                          <div className="text-xs text-slate-500 truncate">{asistencia.evento?.nombre}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-700">{asistencia.deportista?.cedula || '—'}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-700">{asistencia.deportista?.grupo_nombre || '—'}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-700">{asistencia.evento?.nombre || '—'}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-700">{asistencia.evento?.fecha_inicio ? new Date(asistencia.evento.fecha_inicio).toLocaleString('es-EC') : '—'}</td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <Badge variant={
                        asistencia.estado_asistencia === ESTADOS_ASISTENCIA.PRESENTE ? "success" :
                        asistencia.estado_asistencia === ESTADOS_ASISTENCIA.AUSENTE ? "danger" :
                        asistencia.estado_asistencia === ESTADOS_ASISTENCIA.TARDANZA ? "warning" :
                        asistencia.estado_asistencia === ESTADOS_ASISTENCIA.JUSTIFICADO ? "info" : "default"
                      }>{asistencia.estado_asistencia}</Badge>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center justify-center gap-2">
                        <button onClick={() => openDetail(asistencia)} className="inline-flex items-center justify-center rounded-lg border border-blue-600 text-blue-600 px-3 py-1.5 text-xs font-medium hover:bg-blue-50 hover:border-blue-400 hover:text-blue-500 transition">Ver</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {filtered.length === 0 && (
            <div className="text-center py-12 text-slate-500">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-slate-100 mx-auto mb-4">
                <InboxIcon className="w-8 h-8 text-slate-400" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-1">No se encontraron asistencias</h3>
              <p className="text-sm text-slate-500">{query ? "Intenta ajustar los filtros de búsqueda" : "No hay asistencias registradas"}</p>
            </div>
          )}
        </div>
      )}

      <Modal
        open={showDetail && !!selected}
        onCancel={() => { setShowDetail(false); setSelected(null); }}
        title="Detalle de asistencia"
        footer={null}
      >
        {selected && (
          <div className="space-y-4">
            <div className="rounded-xl border border-slate-200 p-4">
              <h4 className="text-sm font-semibold text-slate-700 mb-2">Deportista</h4>
              <div className="space-y-1 text-sm">
                <div className="grid grid-cols-2 gap-2">
                  <span className="text-slate-500">Nombre:</span>
                  <span className="font-medium">{selected.deportista?.nombres} {selected.deportista?.apellidos}</span>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <span className="text-slate-500">Cédula:</span>
                  <span>{selected.deportista?.cedula || "—"}</span>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <span className="text-slate-500">Grupo:</span>
                  <span>{selected.deportista?.grupo_nombre || "Sin grupo"}</span>
                </div>
              </div>
            </div>

            <div className="rounded-xl border border-slate-200 p-4">
              <h4 className="text-sm font-semibold text-slate-700 mb-2">Evento</h4>
              <div className="space-y-1 text-sm">
                <div className="grid grid-cols-2 gap-2">
                  <span className="text-slate-500">Nombre:</span>
                  <span className="font-medium">{selected.evento?.nombre || "—"}</span>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <span className="text-slate-500">Tipo:</span>
                  <Badge variant={selected.evento?.tipo === TIPO_EVENTO.TORNEO ? "info" : "muted"}>
                    {selected.evento?.tipo === TIPO_EVENTO.ENTRENAMIENTO ? "Entrenamiento" : "Torneo"}
                  </Badge>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <span className="text-slate-500">Fecha inicio:</span>
                  <span>{selected.evento?.fecha_inicio ? new Date(selected.evento.fecha_inicio).toLocaleString('es-EC') : "—"}</span>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <span className="text-slate-500">Fecha fin:</span>
                  <span>{selected.evento?.fecha_fin ? new Date(selected.evento.fecha_fin).toLocaleString('es-EC') : "—"}</span>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <span className="text-slate-500">Ubicación:</span>
                  <span>{selected.evento?.ubicacion || "—"}</span>
                </div>
              </div>
            </div>

            <div className="rounded-xl border border-slate-200 p-4">
              <h4 className="text-sm font-semibold text-slate-700 mb-2">Registro de asistencia</h4>
              <div className="space-y-1 text-sm">
                <div className="grid grid-cols-2 gap-2">
                  <span className="text-slate-500">Estado:</span>
                  <Badge variant={
                    selected.estado_asistencia === ESTADOS_ASISTENCIA.PRESENTE ? "success" :
                    selected.estado_asistencia === ESTADOS_ASISTENCIA.AUSENTE ? "danger" :
                    selected.estado_asistencia === ESTADOS_ASISTENCIA.TARDANZA ? "warning" :
                    selected.estado_asistencia === ESTADOS_ASISTENCIA.JUSTIFICADO ? "info" : "default"
                  }>
                    {selected.estado_asistencia === ESTADOS_ASISTENCIA.PRESENTE ? "Presente" :
                     selected.estado_asistencia === ESTADOS_ASISTENCIA.AUSENTE ? "Ausente" :
                     selected.estado_asistencia === ESTADOS_ASISTENCIA.TARDANZA ? "Tardanza" :
                     selected.estado_asistencia === ESTADOS_ASISTENCIA.JUSTIFICADO ? "Justificado" : 
                     selected.estado_asistencia}
                  </Badge>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <span className="text-slate-500">Fecha registro:</span>
                  <span>{selected.fecha_registro ? new Date(selected.fecha_registro).toLocaleString('es-EC') : "—"}</span>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <span className="text-slate-500">Registrado por:</span>
                  <span>{selected.registrada_por_colaborador?.nombres} {selected.registrada_por_colaborador?.apellidos}</span>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <span className="text-slate-500">Email colaborador:</span>
                  <span className="text-xs">{selected.registrada_por_colaborador?.email || "—"}</span>
                </div>
                {selected.observaciones && (
                  <div className="grid grid-cols-2 gap-2">
                    <span className="text-slate-500">Observaciones:</span>
                    <span className="italic">"{selected.observaciones}"</span>
                  </div>
                )}
              </div>
            </div>

            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => { setShowDetail(false); setSelected(null); }}
                className="rounded-xl border border-neutral-300 px-4 py-2 text-sm hover:bg-neutral-100"
              >
                Cerrar
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
